import { inject, type Ref, ref } from "vue";
import type { IOptionSetValue, IRecordWithUnit, IService } from "../service";
import type { IRecord } from "./FormRepository";

export interface IMassnahmenHauptkategorie {
  id?: string;
  name?: string;
  reihenfolge?: number;
}

export interface IEnergietraeger {
  id?: string;
  name?: string;
  einheit?: string; // EntityReference.
  umrechnungCO2?: number;
  umrechnungKWH?: number;
  umrechnungDirekt: number;
  dichte?: number;
}

export interface IReinigungsmittelvorkehrung {
  id?: string;
  name?: string;
  beschreibung?: string;
}

export interface IStaticDataRepository {
  // getEinheiten(): Promise<IEinheit[]>;  
  getProduktionsrueckstaende(): Promise<IRecordWithUnit[]>;  
  getEinsatzstoffe(): Promise<IRecordWithUnit[]>;  
  getMassnahmenhauptkategorien(): Promise<IMassnahmenHauptkategorie[]>;
  getMassnahmenkategorien(): Promise<IOptionSetValue[]>;
  getWirtschaftlicheAuswirkungen(): Promise<IOptionSetValue[]>;
  getEnergietraeger(): Promise<IEnergietraeger[]>;
  getErhebungsmethoden(): Promise<IOptionSetValue[]>;
  getUmweltwirkungskategorien(): Promise<IRecord[]>;
  getFreigabestatus(): Promise<IOptionSetValue[]>;
  // getAbfallarten(): Promise<IAbfallart[]>;
  getBehaelterarten(): Promise<IRecord[]>;
  getEntsorger(): Promise<IRecord[]>;
  getZwecke(): Promise<IOptionSetValue[]>;
  getGebaeudeZwecke(): Promise<IOptionSetValue[]>;
  getEnergietraeger(): Promise<IEnergietraeger[]>;
  getReinigunsmittelvorkehrungen(): Promise<IReinigungsmittelvorkehrung[]>;
  getAuflagentypen(): Promise<IOptionSetValue[]>;
  getBranchen(): Promise<IRecord[]>;
}

export const StaticDataSymbol = Symbol();

export class StaticDataRepository implements IStaticDataRepository {
  private _service: IService;
  // private _einheiten: Ref<IEinheit[]>;
  private _produktionsrueckstaende: Ref<IRecordWithUnit[]>
  private _einsatzstoffe: Ref<IRecordWithUnit[]>;
  private _massnahmenHauptkategorien: Ref<IMassnahmenHauptkategorie[]>;
  private _massnahmenKategorien: Ref<IOptionSetValue[]>;
  private _wirtschaftlicheAuswirkungen: Ref<IOptionSetValue[]>;
  private _energietraeger: Ref<IEnergietraeger[]>;
  private _erhebungsmethoden: Ref<IOptionSetValue[]>;
  private _umweltwirkungskategorien: Ref<IRecord[]>;
  private _freigabestatus: Ref<IOptionSetValue[]>;
  // private _abfallarten: Ref<IAbfallart[]>;
  private _behaelterart: Ref<IRecord[]>;
  private _entsorger: Ref<IRecord[]>;
  private _zwecke: Ref<IOptionSetValue[]>;
  private _gebaeudezwecke: Ref<IOptionSetValue[]>;
  private _reinigungsmittelVorkehrungen: Ref<IReinigungsmittelvorkehrung[]>;
  private _auflagentypen: Ref<IOptionSetValue[]>;
  private _branchen: Ref<IRecord[]>;

  constructor(service: IService) {
    this._service = service;
    // this._einheiten = ref(new Array<IEinheit>);
    this._produktionsrueckstaende = ref(new Array<IRecordWithUnit>);
    this._einsatzstoffe = ref(new Array<IRecordWithUnit>);
    this._massnahmenHauptkategorien = ref(new Array<IMassnahmenHauptkategorie>);
    this._massnahmenKategorien = ref(new Array<IOptionSetValue>);
    this._wirtschaftlicheAuswirkungen = ref(new Array<IOptionSetValue>);
    this._energietraeger = ref(new Array<IEnergietraeger>);
    this._erhebungsmethoden = ref(new Array<IOptionSetValue>);
    this._umweltwirkungskategorien = ref(new Array<IRecord>);
    this._freigabestatus = ref(new Array<IOptionSetValue>);
    // this._abfallarten = ref(new Array<IAbfallart>);
    this._behaelterart = ref(new Array<IRecord>);
    this._entsorger = ref(new Array<IRecord>);
    this._zwecke = ref(new Array<IOptionSetValue>);
    this._gebaeudezwecke = ref(new Array<IOptionSetValue>);
    this._reinigungsmittelVorkehrungen = ref(new Array<IReinigungsmittelvorkehrung>);
    this._auflagentypen = ref(new Array<IOptionSetValue>);
    this._branchen = ref(new Array<IRecord>);
  }

  async getProduktionsrueckstaende(): Promise<IRecordWithUnit[]> {
    if (this._produktionsrueckstaende.value?.length === 0) {
      this._produktionsrueckstaende.value = await this._service.get("/staticdata/produktionsrueckstand", true);
    }

    return this._produktionsrueckstaende.value;
  }

  async getEinsatzstoffe(): Promise<IRecordWithUnit[]> {
    if (this._einsatzstoffe.value?.length === 0) {
      this._einsatzstoffe.value = await this._service.get("/staticdata/einsatzstoffe", true);
    }

    return this._einsatzstoffe.value;
  }

  async getMassnahmenhauptkategorien(): Promise<IMassnahmenHauptkategorie[]> {
    if (this._massnahmenHauptkategorien.value?.length === 0) {
      this._massnahmenHauptkategorien.value = await this._service.get("/staticdata/massnahmenhauptkategorie", true);
    }

    return this._massnahmenHauptkategorien.value;
  }

  async getMassnahmenkategorien(): Promise<IOptionSetValue[]> {
    if (this._massnahmenKategorien.value?.length === 0) {
      this._massnahmenKategorien.value = await this._service.get("/staticdata/massnahmenkategorie", true);
    }

    return this._massnahmenKategorien.value;
  }

  async getWirtschaftlicheAuswirkungen(): Promise<IOptionSetValue[]> {
    if (this._wirtschaftlicheAuswirkungen.value?.length === 0) {
      this._wirtschaftlicheAuswirkungen.value = await this._service.get("/staticdata/wirtschaftlicheauswirkungen", true);
    }

    return this._wirtschaftlicheAuswirkungen.value;
  }

  async getEnergietraeger(): Promise<IEnergietraeger[]> {
    if (this._energietraeger.value?.length === 0) {
      this._energietraeger.value = await this._service.get("/staticdata/energietraeger", true);
    }

    return this._energietraeger.value;
  }

  async getErhebungsmethoden(): Promise<IOptionSetValue[]> {
    if (this._erhebungsmethoden.value?.length === 0) {
      this._erhebungsmethoden.value = await this._service.get("/staticdata/erhebnungsmethode", true);
    }

    return this._erhebungsmethoden.value;
  }

  async getUmweltwirkungskategorien(): Promise<IRecord[]> {
    if (this._umweltwirkungskategorien.value?.length === 0) {
      this._umweltwirkungskategorien.value = await this._service.get("/staticdata/umweltwirkungskategorie", true);
    }

    return this._umweltwirkungskategorien.value;
  }

  async getFreigabestatus(): Promise<IOptionSetValue[]> {
    if (this._freigabestatus.value?.length === 0) {
      this._freigabestatus.value = await this._service.get("/staticdata/freigabestatus", true);
    }

    return this._freigabestatus.value;
  }

  async getBehaelterarten(): Promise<IRecord[]> {
    if (this._behaelterart.value?.length === 0) {
      this._behaelterart.value = await this._service.get("/staticdata/behaelterart", true);
    }

    return this._behaelterart.value;
  }

  async getEntsorger(): Promise<IRecord[]> {
    if (this._entsorger.value?.length === 0) {
      this._entsorger.value = await this._service.get("/staticdata/entsorger", true);
    }

    return this._entsorger.value;
  }

  async getZwecke(): Promise<IOptionSetValue[]> {
    if (this._zwecke.value?.length === 0) {
      this._zwecke.value = await this._service.get("/staticdata/zweck", true);
    }

    return this._zwecke.value;
  }

  async getGebaeudeZwecke(): Promise<IOptionSetValue[]> {
    if (this._gebaeudezwecke.value?.length === 0) {
      this._gebaeudezwecke.value = await this._service.get("/staticdata/gebaeudezweck", true);
    }

    return this._gebaeudezwecke.value;
  }

  async getReinigunsmittelvorkehrungen(): Promise<IReinigungsmittelvorkehrung[]> {
    if (this._reinigungsmittelVorkehrungen.value?.length === 0) {
      this._reinigungsmittelVorkehrungen.value = await this._service.get("/staticdata/reinigungsmittelvorkehrungen", true);
    }

    return this._reinigungsmittelVorkehrungen.value;
  }

  async getAuflagentypen(): Promise<IOptionSetValue[]> {
    if (this._auflagentypen.value?.length === 0) {
      this._auflagentypen.value = await this._service.get("/staticdata/auflagentyp", true);
    }

    return this._auflagentypen.value;
  }

  async getBranchen(): Promise<IRecord[]> {
    if (this._branchen.value?.length === 0) {
      this._branchen.value = await this._service.get("/staticdata/branchen", true);
    }

    return this._branchen.value;
  }
}

export const useStaticData = (): IStaticDataRepository => {
  return inject(StaticDataSymbol) as IStaticDataRepository;
}